import React, { useEffect, useRef } from 'react';
import { SegmentationLabel } from 'models/segmentation';
import { Segmentation } from 'models/predictions';
import { cleanDartPath, pointsToSvgPath } from '../../../utils/segPath';

interface DifferenceOverlayProps {
  leftSegmentations: (SegmentationLabel | Segmentation)[];
  rightSegmentations: (SegmentationLabel | Segmentation)[];
  imageDimensions: { width: number; height: number };
  visibleLeftSegmentations: Set<string>;
  visibleRightSegmentations: Set<string>;
  highlightedSegmentationClass?: string | null;
  highlightedSegmentationPathsLeft?: string[] | null;
  highlightedSegmentationPathsRight?: string[] | null;
  highlightedSegmentationFillColorLeft?: string | null;
  highlightedSegmentationFillColorRight?: string | null;
}

// Detect whether path is in custom backend format or standard SVG format
function detectPathFormat(path: string): 'custom' | 'standard' {
  const qCommands = path.match(/Q[^QML]+/g);
  if (!qCommands || qCommands.length === 0) return 'standard';

  const firstQ = qCommands[0];
  const coords = firstQ.match(/[-0-9.]+/g);

  return coords && coords.length >= 4 ? 'standard' : 'custom';
}

// Parse standard SVG format
function parseStandardPathFormat(pathString: string): string {
  return pathString
    .replace(/([MLCQZHVSA])\s*,/gi, '$1 ')
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Normalize segmentation path
const normalizeSegPath = (raw: string): string => {
  if (!raw || raw.trim() === '') return '';

  const format = detectPathFormat(raw);

  if (format === 'custom') {
    // Use a larger maxJump to avoid trimming long continuous contours that
    // include intermediate coordinate jumps; this prevents shapes from being cut.
    const pts = cleanDartPath(raw, 100);
    return pts && pts.length ? pointsToSvgPath(pts) : '';
  } else {
    return parseStandardPathFormat(raw);
  }
};

// Convert hex color like "#rrggbb" to [r,g,b] numbers 0-255. Returns null on failure.
const hexToRgb = (hex?: string | null): [number, number, number] | null => {
  if (!hex || typeof hex !== 'string') return null;
  const h = hex.replace('#', '').trim();
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return [r, g, b];
  }
  if (h.length === 6) {
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return [r, g, b];
  }
  return null;
};

// Draw SVG path on canvas context
const drawPathOnCanvas = (ctx: CanvasRenderingContext2D, pathStr: string) => {
  if (!pathStr) return;

  // Parse SVG path commands - handle M, L, Z, and approximate curves with lines
  const commands = pathStr.match(/[MLCQZHVSA][^MLCQZHVSA]*/gi) || [];
  ctx.beginPath();

  let currentX = 0;
  let currentY = 0;

  for (const cmd of commands) {
    const type = cmd[0].toUpperCase();
    const coords = cmd
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .map(Number)
      .filter(n => !isNaN(n));

    if (type === 'M' && coords.length >= 2) {
      currentX = coords[0];
      currentY = coords[1];
      ctx.moveTo(currentX, currentY);
      // Handle implicit lineTo after moveTo
      for (let i = 2; i < coords.length; i += 2) {
        if (i + 1 < coords.length) {
          currentX = coords[i];
          currentY = coords[i + 1];
          ctx.lineTo(currentX, currentY);
        }
      }
    } else if (type === 'L' && coords.length >= 2) {
      for (let i = 0; i < coords.length; i += 2) {
        if (i + 1 < coords.length) {
          currentX = coords[i];
          currentY = coords[i + 1];
          ctx.lineTo(currentX, currentY);
        }
      }
    } else if (type === 'Z') {
      ctx.closePath();
    } else if ((type === 'C' || type === 'Q') && coords.length >= 4) {
      // Approximate curves with line segments (for cubic/quadratic bezier)
      // This is a simplification - for better accuracy, we'd need proper bezier rendering
      const endX = coords[coords.length - 2];
      const endY = coords[coords.length - 1];
      ctx.lineTo(endX, endY);
      currentX = endX;
      currentY = endY;
    }
  }
};

// Check if segmentation matches the highlighted class
const matchesHighlightedClass = (
  seg: SegmentationLabel | Segmentation,
  highlightedClass?: string | null
): boolean => {
  if (!highlightedClass) return true;

  const segClassRaw =
    (seg as any).segmentationClassValue ??
    (seg as any).segmentationClass ??
    (seg as any).segmentation_class ??
    null;

  const normalizedSegClass =
    typeof segClassRaw === 'string' ? segClassRaw.trim().toLowerCase() : null;
  const normalizedHighlightedClass =
    typeof highlightedClass === 'string' ? highlightedClass.trim().toLowerCase() : null;

  return (
    normalizedSegClass !== null &&
    normalizedHighlightedClass !== null &&
    normalizedSegClass === normalizedHighlightedClass
  );
};

const DifferenceOverlay: React.FC<DifferenceOverlayProps> = ({
  leftSegmentations,
  rightSegmentations,
  imageDimensions,
  visibleLeftSegmentations,
  visibleRightSegmentations,
  highlightedSegmentationClass,
  highlightedSegmentationPathsLeft,
  highlightedSegmentationPathsRight,
  highlightedSegmentationFillColorLeft,
  highlightedSegmentationFillColorRight,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || imageDimensions.width === 0 || imageDimensions.height === 0) return;

    // Determine which paths to draw for GT and AI.
    // Prefer explicit highlighted paths when provided, otherwise draw the union
    // of all visible segmentations' paths (respecting highlighted class filter).
    const collectPathsFromSegmentations = (
      segs: (SegmentationLabel | Segmentation)[],
      visibleSet: Set<string>
    ): string[] => {
      if (!segs || segs.length === 0) return [];

      const paths: string[] = [];
      segs.forEach(s => {
        const icid = (s as any)?.icid;
        if (!icid) return;
        if (!visibleSet.has(icid)) return;
        const rawPaths = Array.isArray((s as any).paths) ? (s as any).paths : [];
        rawPaths.forEach((p: any) => {
          const normalized = normalizeSegPath(String(p || ''));
          if (normalized) paths.push(normalized);
        });
      });
      return paths;
    };

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match image dimensions exactly for proper resolution
    canvas.width = imageDimensions.width;
    canvas.height = imageDimensions.height;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create offscreen canvases for GT and AI masks with exact resolution
    const gtCanvas = document.createElement('canvas');
    gtCanvas.width = imageDimensions.width;
    gtCanvas.height = imageDimensions.height;
    const gtCtx = gtCanvas.getContext('2d');
    if (!gtCtx) return;

    const aiCanvas = document.createElement('canvas');
    aiCanvas.width = imageDimensions.width;
    aiCanvas.height = imageDimensions.height;
    const aiCtx = aiCanvas.getContext('2d');
    if (!aiCtx) return;

    // Determine paths to draw for GT and AI.
    const leftPathsToDraw =
      highlightedSegmentationPathsLeft && highlightedSegmentationPathsLeft.length > 0
        ? highlightedSegmentationPathsLeft.map(p => normalizeSegPath(String(p))).filter(Boolean)
        : collectPathsFromSegmentations(leftSegmentations || [], visibleLeftSegmentations);

    const rightPathsToDraw =
      highlightedSegmentationPathsRight && highlightedSegmentationPathsRight.length > 0
        ? highlightedSegmentationPathsRight.map(p => normalizeSegPath(String(p))).filter(Boolean)
        : collectPathsFromSegmentations(rightSegmentations || [], visibleRightSegmentations);

    // If nothing to draw on either side, clear and exit.
    if (
      (!leftPathsToDraw || leftPathsToDraw.length === 0) &&
      (!rightPathsToDraw || rightPathsToDraw.length === 0)
    ) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    // Draw GT mask using collected left paths
    gtCtx.fillStyle = 'white';
    leftPathsToDraw.forEach(pathStr => {
      if (!pathStr) return;
      drawPathOnCanvas(gtCtx, pathStr);
      gtCtx.fill();
    });

    // Draw AI mask using collected right paths
    aiCtx.fillStyle = 'white';
    rightPathsToDraw.forEach(pathStr => {
      if (!pathStr) return;
      drawPathOnCanvas(aiCtx, pathStr);
      aiCtx.fill();
    });

    // Get image data for both masks
    const gtImageData = gtCtx.getImageData(0, 0, canvas.width, canvas.height);
    const aiImageData = aiCtx.getImageData(0, 0, canvas.width, canvas.height);

    // Calculate difference
    // False positives (AI-only) use prediction fill color; false negatives (GT-only) use label fill color.
    const diffImageData = ctx.createImageData(canvas.width, canvas.height);

    // derive colors (RGB) from provided hex overrides or fallbacks
    // Prefer explicit per-side fill overrides, otherwise use the first visible region's segmentationColor.
    const leftRegionColorHex =
      highlightedSegmentationFillColorLeft ||
      (
        (leftSegmentations || []).find(
          s =>
            visibleLeftSegmentations.has((s as any).icid) &&
            matchesHighlightedClass(s, highlightedSegmentationClass)
        ) as any
      )?.segmentationColor ||
      undefined;
    const rightRegionColorHex =
      highlightedSegmentationFillColorRight ||
      (
        (rightSegmentations || []).find(
          s =>
            visibleRightSegmentations.has((s as any).icid) &&
            matchesHighlightedClass(s, highlightedSegmentationClass)
        ) as any
      )?.segmentationColor ||
      undefined;

    const gtRgb = hexToRgb(leftRegionColorHex) || hexToRgb('#00ff00') || [0, 255, 0];
    const aiRgb = hexToRgb(rightRegionColorHex) || hexToRgb('#ff0000') || [255, 0, 0];

    const alpha = 200; // opacity in 0-255

    for (let i = 0; i < gtImageData.data.length; i += 4) {
      const gtValue = gtImageData.data[i]; // R channel (white = 255)
      const aiValue = aiImageData.data[i]; // R channel (white = 255)

      const isGT = gtValue > 128;
      const isAI = aiValue > 128;

      if (isAI && !isGT) {
        // False positive: AI predicted but not in GT (use AI/prediction color)
        diffImageData.data[i] = aiRgb[0];
        diffImageData.data[i + 1] = aiRgb[1];
        diffImageData.data[i + 2] = aiRgb[2];
        diffImageData.data[i + 3] = alpha;
      } else if (isGT && !isAI) {
        // False negative: GT has it but AI didn't predict (use GT/label color)
        diffImageData.data[i] = gtRgb[0];
        diffImageData.data[i + 1] = gtRgb[1];
        diffImageData.data[i + 2] = gtRgb[2];
        diffImageData.data[i + 3] = alpha;
      } else {
        // No difference
        diffImageData.data[i] = 0;
        diffImageData.data[i + 1] = 0;
        diffImageData.data[i + 2] = 0;
        diffImageData.data[i + 3] = 0;
      }
    }

    ctx.putImageData(diffImageData, 0, 0);
  }, [
    leftSegmentations,
    rightSegmentations,
    imageDimensions,
    visibleLeftSegmentations,
    visibleRightSegmentations,
    highlightedSegmentationClass,
    highlightedSegmentationPathsLeft,
    highlightedSegmentationPathsRight,
  ]);

  if (imageDimensions.width === 0 || imageDimensions.height === 0) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 5,
        objectFit: 'contain', // Preserve aspect ratio and resolution
      }}
    />
  );
};

export default DifferenceOverlay;
